"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { Typography } from "@repo/ui/aops-components/typography";
import { useAcademyUrl } from "@repo/test-server-url-resolver/hooks/useAcademyUrl";
import { ArrowUpRight, MapPin } from "lucide-react";
import { CAMPUS_SEARCH_MAX_DISTANCE_MILES, type CampusWithDistance } from "../location";
import styles from "./CampusList.module.css";
import { formatCampusAddress, formatDistanceMiles, getCampusMapUrl, groupCampusesByRegion } from "./CampusListUtils";
export type CampusListProps = {
  campuses: CampusWithDistance[];
  /** Campuses currently highlighted in the modal (pending confirmation). */
  highlightedCampusIds: readonly string[];
  /** When true, cards toggle independently; when false, one at a time. */
  multiple?: boolean;
  viewOnMapText: string;
  campusDetailsText?: string;
  showAllCampusesText?: string;
  resultsWithinText: string;
  locationsFoundOneText: string;
  locationsFoundManyText: string;
  unknownRegionText: string;
  distanceMilesText: string;
  /** When false, hides the campus details link. Defaults to true. */
  showCampusDetails?: boolean;
  /** When false, hides the map link. Defaults to true. */
  showViewOnMap?: boolean;
  /**
   * `contained` (default): list scrolls inside the parent (modal).
   * `page`: list grows with content for in-page layouts (rec-tool).
   */
  listLayout?: "contained" | "page";
  onCampusHighlight: (campusId: string) => void;
  onShowAllCampuses?: () => void;
  /** When set, show a search-results summary above the list. */
  searchResults?: {
    /** True for city/ZIP searches limited to {@link CAMPUS_SEARCH_MAX_DISTANCE_MILES}. */
    isDistanceLimited: boolean;
  } | null;
  /** Replaces the campus list body (results header still shows when searching). */
  emptyState?: ReactNode;
  /** When this value changes, the scrollable list resets to the top. */
  listScrollKey?: string;
};

/**
 * Renders selectable campus cards grouped by region.
 */
export function CampusList({
  campuses,
  highlightedCampusIds,
  multiple = false,
  viewOnMapText,
  campusDetailsText = "Campus Details",
  showAllCampusesText = "Show all campuses",
  resultsWithinText,
  locationsFoundOneText,
  locationsFoundManyText,
  unknownRegionText,
  distanceMilesText,
  showCampusDetails = true,
  showViewOnMap = true,
  listLayout = "contained",
  onCampusHighlight,
  onShowAllCampuses,
  searchResults = null,
  emptyState = null,
  listScrollKey = ""
}: CampusListProps) {
  const academyUrl = useAcademyUrl();
  const firstHighlightedRef = useRef<HTMLLIElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const isPageLayout = listLayout === "page";
  const showLinks = showCampusDetails || showViewOnMap;
  const regionGroups = groupCampusesByRegion(campuses, unknownRegionText);
  const showRegionHeaders = regionGroups.length > 1;
  const showDistance = Boolean(searchResults?.isDistanceLimited);
  const highlightedSet = new Set(highlightedCampusIds);
  const count = String(campuses.length);
  const resultsCountLabel = (campuses.length === 1 ? locationsFoundOneText : locationsFoundManyText).replace("{{count}}", count);
  const searchResultsHeading = searchResults ? searchResults.isDistanceLimited ? resultsWithinText.replace("{{miles}}", String(CAMPUS_SEARCH_MAX_DISTANCE_MILES)).replace("{{countLabel}}", resultsCountLabel) : resultsCountLabel : null;
  useEffect(() => {
    if (isPageLayout) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [listScrollKey, isPageLayout]);
  useEffect(() => {
    if (!highlightedCampusIds.length || emptyState || isPageLayout) {
      return;
    }
    const frameId_0 = requestAnimationFrame(() => {
      firstHighlightedRef.current?.scrollIntoView({
        behavior: "auto",
        block: "center"
      });
    });
    return () => {
      cancelAnimationFrame(frameId_0);
    };
  }, [highlightedCampusIds, emptyState, isPageLayout]);
  let firstHighlightedAssigned = false;
  const rootClassName = [styles.campusListRoot, isPageLayout ? styles.campusListRootPage : ""].filter(Boolean).join(" ");
  const listClassName = [styles.campusList, isPageLayout ? styles.campusListPage : ""].filter(Boolean).join(" ");
  return <div className={rootClassName}>
			{searchResultsHeading ? <div className={styles.searchResultsHeader}>
					<Typography as="h3" variant="eyebrow" className={styles.regionHeading}>
						{searchResultsHeading}
					</Typography>
					{onShowAllCampuses ? <button type="button" className={styles.showAllCampusesLink} onClick={onShowAllCampuses}>
							<Typography as="span" variant="buttonSm">
								{showAllCampusesText}
							</Typography>
						</button> : null}
				</div> : null}
			{emptyState ? <div className={styles.emptyStateSlot}>{emptyState}</div> : <ul ref={listRef} className={listClassName}>
					{regionGroups.map(({
        region,
        campuses: regionCampuses
      }) => <li key={region} className={styles.regionGroup}>
							{showRegionHeaders ? <Typography as="h3" variant="eyebrow" className={styles.regionHeading}>
									{region}
								</Typography> : null}
							<ul className={styles.regionCampusList}>
								{regionCampuses.map(({
            campus,
            distanceMiles
          }) => {
            const mapUrl = getCampusMapUrl(campus);
            const campusDetailsUrl = `${academyUrl}/campus/${campus.campus_id}`;
            const isSelected = highlightedSet.has(campus.campus_id);
            const assignFirstRef = isSelected && !firstHighlightedAssigned;
            if (assignFirstRef) {
              firstHighlightedAssigned = true;
            }
            const formattedAddress = formatCampusAddress(campus);
            const distanceLabel = showDistance && distanceMiles != null ? distanceMilesText.replace("{{miles}}", formatDistanceMiles(distanceMiles)) : null;
            return <li key={campus.campus_id} ref={assignFirstRef ? firstHighlightedRef : null}>
											<article className={`${styles.campusCard}${isSelected ? ` ${styles.campusCardSelected}` : ""}`}>
												<button type="button" className={styles.campusSelectButton} aria-pressed={isSelected} aria-label={multiple ? isSelected ? `Deselect ${campus.short_name}` : `Select ${campus.short_name}` : campus.short_name} onClick={() => {
                  onCampusHighlight(campus.campus_id);
                }}>
													<span className={styles.campusCardHeader}>
														<span className={styles.campusCardTitleRow}>
															<Typography as="span" variant="body" className={styles.campusName}>
																{campus.short_name}
															</Typography>
															{distanceLabel ? <span className={styles.campusDistance}>
																	<MapPin aria-hidden className={styles.campusDistanceIcon} size={16} />
																	<Typography as="span" variant="body" className={styles.campusDistanceText}>
																		{distanceLabel}
																	</Typography>
																</span> : null}
														</span>
														{formattedAddress ? <Typography as="span" variant="body" className={styles.campusAddress}>
																{formattedAddress}
															</Typography> : null}
													</span>
												</button>
												{showLinks ? <div className={styles.campusLinks}>
														{showCampusDetails ? <a className={styles.campusLink} href={campusDetailsUrl} target="_blank" rel="noopener noreferrer">
																<Typography as="span" variant="buttonSm">
																	{campusDetailsText}
																</Typography>
																<ArrowUpRight aria-hidden size={16} />
															</a> : null}
														{showViewOnMap && mapUrl ? <a className={styles.campusLink} href={mapUrl} target="_blank" rel="noopener noreferrer">
																<Typography as="span" variant="buttonSm">
																	{viewOnMapText}
																</Typography>
																<ArrowUpRight aria-hidden size={16} />
															</a> : null}
													</div> : null}
											</article>
										</li>;
          })}
							</ul>
						</li>)}
				</ul>}
		</div>;
}